/**
 * GlossaryImportDialog Component
 * Handles glossary import from file or pasted JSON
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Upload, Loader2, AlertCircle, CheckCircle, Eye } from 'lucide-react';

const API = (window.location.origin || '') + '/api';

const GlossaryImportDialog = ({ 
  open, 
  onOpenChange, 
  getAuthHeaders, 
  onImportSuccess 
}) => {
  const [importMergeStrategy, setImportMergeStrategy] = useState('skip');
  const [importJsonText, setImportJsonText] = useState('');
  const [importMethod, setImportMethod] = useState('file');
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewError, setImportPreviewError] = useState('');

  // Preview import data
  const handlePreviewImport = () => {
    if (!importJsonText.trim()) {
      setImportPreviewError('Bitte JSON-Inhalt einfügen');
      return;
    }
    
    setImportPreviewError('');
    
    try {
      const data = JSON.parse(importJsonText);
      const termsArray = Array.isArray(data) ? data : (data.terms || [data]);
      
      const analysis = {
        total: termsArray.length,
        valid: 0,
        invalid: 0,
        withContexts: 0,
        categories: {},
        terms: [],
        errors: []
      };
      
      termsArray.forEach((term, index) => {
        const termInfo = {
          index,
          term: term.term || 'Unbekannt',
          hasDefinition: !!term.master_definition,
          contexts: term.context_definitions ? Object.keys(term.context_definitions) : [],
          tags: term.tags || [],
          isValid: true,
          errors: []
        };
        
        if (!term.term || typeof term.term !== 'string') {
          termInfo.isValid = false;
          termInfo.errors.push('Begriff fehlt');
        }
        if (!term.master_definition || typeof term.master_definition !== 'string') {
          termInfo.isValid = false;
          termInfo.errors.push('Master-Definition fehlt');
        }
        
        if (termInfo.isValid) {
          analysis.valid++;
        } else {
          analysis.invalid++;
          analysis.errors.push(`#${index + 1} "${term.term || 'Unbekannt'}": ${termInfo.errors.join(', ')}`);
        }
        
        if (termInfo.contexts.length > 0) {
          analysis.withContexts++;
        }
        
        const cat = term.category || 'General';
        analysis.categories[cat] = (analysis.categories[cat] || 0) + 1;
        
        analysis.terms.push(termInfo);
      });
      
      setImportPreview(analysis);
    } catch (error) {
      setImportPreviewError('JSON-Parsing-Fehler: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  // Process import
  const processImport = async (jsonText) => {
    try {
      const data = JSON.parse(jsonText);
      
      const res = await fetch(`${API}/glossary/import`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          terms: Array.isArray(data) ? data : (data.terms || [data]),
          merge_strategy: importMergeStrategy
        })
      });
      
      const result = await res.json();
      
      if (!res.ok) {
        throw new Error(result.detail || 'Import fehlgeschlagen');
      }
      
      toast.success(`Import erfolgreich: ${result.imported || 0} Begriffe importiert`);
      onImportSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(error.message || 'Import fehlgeschlagen');
    }
  };

  // Import from preview
  const handleImportFromPreview = async () => {
    if (!importPreview || importPreview.valid === 0) return;
    
    setImportLoading(true);
    try {
      await processImport(importJsonText);
      setImportPreview(null);
    } finally {
      setImportLoading(false);
    }
  };

  // Import from file
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const text = await file.text();
      await processImport(text);
    } catch (error) {
      toast.error('Fehler beim Lesen der Datei');
    } finally {
      setImportLoading(false);
      event.target.value = '';
    }
  };

  // Import from pasted JSON
  const handleImportPaste = async () => {
    if (!importJsonText.trim()) {
      toast.error('Bitte JSON-Inhalt einfügen');
      return;
    }
    
    setImportLoading(true);
    try {
      await processImport(importJsonText);
    } finally {
      setImportLoading(false);
    }
  };

  const handleClose = () => {
    setImportJsonText('');
    setImportPreview(null);
    setImportPreviewError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Glossar importieren</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Merge Strategy */}
          <div className="space-y-2">
            <Label>Merge-Strategie</Label>
            <Select value={importMergeStrategy} onValueChange={setImportMergeStrategy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Bestehende überspringen</SelectItem>
                <SelectItem value="update">Bestehende aktualisieren</SelectItem>
                <SelectItem value="replace">Alles ersetzen</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Import Method Toggle */}
          <div className="flex gap-2">
            <Button 
              variant={importMethod === 'file' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setImportMethod('file')}
            >
              <Upload className="w-4 h-4 mr-2" />
              Datei hochladen
            </Button>
            <Button 
              variant={importMethod === 'paste' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setImportMethod('paste')}
            >
              JSON einfügen
            </Button>
          </div>

          {importMethod === 'file' ? (
            <div className="space-y-2">
              <Label>JSON-Datei auswählen</Label>
              <input
                type="file"
                accept=".json"
                onChange={handleImportFile}
                className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>JSON einfügen</Label>
              <Textarea
                placeholder='[{"term": "Begriff", "master_definition": "Definition..."}]'
                value={importJsonText}
                onChange={(e) => {
                  setImportJsonText(e.target.value);
                  setImportPreview(null);
                  setImportPreviewError('');
                }}
                rows={8}
                className="font-mono text-sm"
              />
              
              {/* Preview Error */}
              {importPreviewError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{importPreviewError}</span>
                </div>
              )}
              
              {/* Preview Results */}
              {importPreview && (
                <div className="p-4 bg-slate-50 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Import-Vorschau</span>
                    <Button variant="ghost" size="sm" onClick={() => setImportPreview(null)}>
                      Schließen
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="p-2 bg-white rounded border">
                      <span className="text-slate-500">Gesamt:</span>{' '}
                      <span className="font-medium">{importPreview.total}</span>
                    </div>
                    <div className="p-2 bg-green-50 rounded border border-green-200">
                      <CheckCircle className="w-3 h-3 inline mr-1 text-green-600" />
                      <span className="text-green-700">{importPreview.valid} gültig</span>
                    </div>
                    <div className="p-2 bg-red-50 rounded border border-red-200">
                      <AlertCircle className="w-3 h-3 inline mr-1 text-red-600" />
                      <span className="text-red-700">{importPreview.invalid} ungültig</span>
                    </div>
                  </div>
                  
                  {importPreview.errors.length > 0 && (
                    <ScrollArea className="h-24 border rounded p-2 bg-white">
                      <ul className="text-xs text-red-600 space-y-1">
                        {importPreview.errors.map((err, i) => (
                          <li key={`error-${i}`}>• {err}</li>
                        ))}
                      </ul>
                    </ScrollArea>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleImportFromPreview}
                      disabled={importLoading || importPreview.valid === 0}
                      className="flex-1"
                    >
                      {importLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {importPreview.valid} Begriffe importieren
                    </Button>
                  </div>
                </div>
              )}
              
              {!importPreview && !importPreviewError && importJsonText.trim() && (
                <Button variant="outline" onClick={handlePreviewImport} className="w-full">
                  <Eye className="w-4 h-4 mr-2" />
                  Vorschau anzeigen
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Abbrechen
          </Button>
          {importMethod === 'paste' && !importPreview && (
            <Button onClick={handleImportPaste} disabled={importLoading || !importJsonText.trim()}>
              {importLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Direkt importieren
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlossaryImportDialog;
